"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { formatDate, formatTime } from "@/shared/dateFormat";
import { useLocale } from "next-intl";

interface PublicStudentHeaderProps {
  fullName: string;
  nameForInitials?: string;
  photo?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  score?: number | null;
  isExternal?: boolean;
}

export function PublicStudentHeader({
  fullName,
  nameForInitials,
  photo,
  startedAt,
  finishedAt,
  score,
  isExternal = false,
}: PublicStudentHeaderProps) {
  const locale = useLocale();
  const initials = (nameForInitials ?? fullName)
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  const timeValue = finishedAt || startedAt || null;
  const timeLabel = timeValue ? formatTime(timeValue, locale) : "";
  const dateLabel = timeValue ? formatDate(timeValue, locale) : "";

  return (
    <div className="flex items-center justify-between w-full">
      {/* Full-width card like original StudentHeader */}
      <div className="flex items-center justify-between bg-white rounded-md border border-gray-200 px-4 py-2 flex-1">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 !rounded-xl">
            <AvatarImage src={photo ?? ""} />
            <AvatarFallback className="!rounded-xl">{initials}</AvatarFallback>
          </Avatar>

          <span className="text-[15px] font-medium text-gray-900">
            {fullName}
          </span>
        </div>

        <div className="flex items-center gap-4">
          {(timeLabel || dateLabel) && (
            <div className="text-sm text-muted-foreground">
              {timeLabel && <span className="text-muted-foreground">{timeLabel}</span>}
              {timeLabel && dateLabel && <span className="mx-2 text-muted-foreground">|</span>}
              {dateLabel && <span className="text-gray-900">{dateLabel}</span>}
            </div>
          )}
          {!isExternal && score !== undefined && score !== null && (
            <div className="flex h-9 items-center gap-2 rounded-md border border-gray-200 bg-white px-4 text-[15px] font-medium text-gray-900">
              <span>{score}</span>
              <span className="text-muted-foreground">{locale.startsWith("en") ? "points" : "балл"}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
